import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MDRBadge } from "@/components/MDRBadge";
import {
  LayoutDashboard, Building2, Hospital, Cpu, Users, BarChart3,
  Menu, ArrowLeftRight, LogOut, Settings, ChevronDown,
  FileText, Layers, BookOpen, Sparkles, UserCog
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3083dfc7-2383-45dd-896b-7ed8090288e6/artifacts/x64h80zt_WITAPP%20-%20Logo%20orizzontale%20-%20Colori%20-%20No%20claim.png";

// Single-level entries (top-level)
const navItems = [
  { path: "/admin", label: "Panoramica", icon: LayoutDashboard, end: true },
  { path: "/admin/pharmacies", label: "Farmacie", icon: Building2 },
  { path: "/admin/structures", label: "Strutture Accreditate", icon: Hospital },
  { path: "/admin/devices", label: "Elettromedicali", icon: Cpu },
  { path: "/admin/users", label: "Responsabili", icon: Users },
  { path: "/admin/monitoring", label: "Monitoraggio", icon: BarChart3 }
];

// Configuration submenu (Fase 7 — Batch 1 active)
const configItems = [
  { path: "/admin/config/exam-types", label: "Tipologie esami", icon: FileText },
  { path: "/admin/config/services", label: "Prestazioni", icon: Layers },
  { path: "/admin/config/bundles", label: "Bundle", icon: BookOpen },
  { path: "/admin/config/pdta", label: "PDTA", icon: Sparkles },
  { path: "/admin/config/users", label: "Utenti", icon: UserCog }
];

export const AdminLayout = () => {
  const navigate = useNavigate();
  const { isAuthenticated, currentUser } = useAppContext();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const NavItem = ({ item, onClick }) => (
    <NavLink
      to={item.path}
      end={item.end}
      onClick={item.soon ? (e) => e.preventDefault() : onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
          isActive ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:bg-muted hover:text-foreground",
          item.soon && "opacity-60 cursor-not-allowed"
        )
      }
    >
      <item.icon className="h-5 w-5" />
      <span className="flex-1">{item.label}</span>
      {item.soon && <span className="text-[9px] uppercase tracking-wider bg-muted px-1.5 py-0.5 rounded">Soon</span>}
    </NavLink>
  );

  const SubNavItem = ({ item, onClick }) => (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium transition-all ml-4",
          isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )
      }
    >
      <item.icon className="h-4 w-4" />
      {item.label}
    </NavLink>
  );

  const Sidebar = ({ onItemClick }) => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b px-4">
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
      </div>
      <div className="p-4 border-b">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 border-2 border-slate-300">
            <AvatarFallback className="bg-slate-200 text-slate-700 font-semibold">
              <Settings className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium text-sm leading-tight truncate">{currentUser?.name || "Admin"}</p>
            <p className="text-xs text-muted-foreground">Amministratore Sistema</p>
          </div>
        </div>
        <MDRBadge variant="admin" size="sm" className="mt-3" />
      </div>
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map(item => <NavItem key={item.path} item={item} onClick={onItemClick} />)}
          <div className="pt-3 mt-2 border-t">
            <div className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-foreground">
              <Settings className="h-5 w-5" />
              <span className="flex-1">Configurazioni</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="space-y-1 mt-1" data-testid="admin-config-submenu">
              {configItems.map(item => <SubNavItem key={item.path} item={item} onClick={onItemClick} />)}
            </div>
          </div>
        </nav>
      </ScrollArea>
      <div className="border-t p-3 space-y-1">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
          onClick={() => navigate("/")}
          data-testid="admin-switch-profile-btn"
        >
          <ArrowLeftRight className="h-4 w-4" />
          Cambia profilo
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-destructive hover:text-destructive"
          onClick={() => navigate("/login")}
        >
          <LogOut className="h-4 w-4" />
          Esci
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r bg-card md:block">
        <Sidebar />
      </aside>
      <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-card px-4 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <Sidebar onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
      </header>
      <main className="md:pl-64">
        <div className="min-h-[calc(100vh-4rem)] md:min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
